import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const G   = "#059669";
const G2  = "#10b981";
const G3  = "#34d399";
const GD  = "#065f46";
const GL  = "#ecfdf5";
const SL  = "#0f172a";
const N8  = "#1e293b";
const N7  = "#334155";
const N6  = "#475569";
const N5  = "#64748b";
const N4  = "#94a3b8";
const N3  = "#cbd5e1";
const N2  = "#e2e8f0";
const N1  = "#f1f5f9";
const N0  = "#f8fafc";
const WHT = "#ffffff";

// ─── HeroDemoCard Config ────────────────────────────────────────────────────────
const T     = { s1: 2400, s2: 2600, s3: 2400, s4: 2800 };
const TOTAL = T.s1 + T.s2 + T.s3 + T.s4;

const BRIEF_TAGS = ["Sustainability Goal", "Reporting Help", "Net Zero Plan", "Impact Check"];

const EXPERTS = [
  { id: 0, sym: "ENV", label: "Environment Expert",   sub: "Reduces your carbon impact",   color: GD        },
  { id: 1, sym: "RPT", label: "Reporting Specialist", sub: "Handles compliance documents",  color: "#0369a1" },
  { id: 2, sym: "STR", label: "Strategy Advisor",     sub: "Builds your action roadmap",   color: "#7c3aed" },
];

const ENG_LINES = [
  { label: "Smart matching", minStep: 2 },
  { label: "Expert vetting", minStep: 3 },
  { label: "Final delivery", minStep: 4 },
];

const STEPS_LABELS = ["Submit", "AI Match", "Review", "Done"];

const PILL_CFG = {
  1: { label: "RECEIVING", bg: N1, border: N2,         dot: N4, text: N5 },
  2: { label: "MATCHING",  bg: N1, border: G2 + "55",  dot: G2, text: N6 },
  3: { label: "REVIEWING", bg: GL, border: G  + "55",  dot: G,  text: GD },
  4: { label: "MATCHED",   bg: GL, border: G  + "88",  dot: G,  text: GD },
};

const STATUS_CFG = {
  1: { dot: N4, msg: "Your project brief received — finding the right experts…" },
  2: { dot: G2, msg: "Our AI is scanning the verified expert network…" },
  3: { dot: G,  msg: "Experts shortlisted — a human is confirming credentials…" },
  4: { dot: G3, msg: "Your expert shortlist is ready — delivered in under 48 h ✓" },
};
// ─── TravelDot ─────────────────────────────────────────────────────────────────
function TravelDot({ active }) {
  const [pos, setPos] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!active) { setPos(0); return; }
    let p = 0;
    const animate = () => {
                
    if (p >= 29) return;
      p += 0.5;
      setPos(p);
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [active]);

  if (!active) return null;
  return (
    <div style={{
      position: "absolute", width: 7, height: 7, borderRadius: "50%",
      background: G, top: "50%", marginTop: -3.5, left: pos,
    }} />
  );
}

// ─── FlowConnector ─────────────────────────────────────────────────────────────
function FlowConnector({ active }) {
  return (
    <div style={{ width: 36, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", height: 60 }}>
      <div style={{
        position: "absolute", left: 0, top: "50%", width: 36, height: 1.5,
        background: G + "55",
        transform: active ? "scaleX(1)" : "scaleX(0)",
        transformOrigin: "0 50%",
        transition: "transform 0.5s ease",
      }} />
      <TravelDot active={active} />
    </div>
  );
}

// ─── TagChip ───────────────────────────────────────────────────────────────────
function TagChip({ label, delay, visible }) {
  return (
    <div style={{
      background: GL, borderRadius: 20, padding: "2px 7px",
      border: `1px solid ${G}30`, alignSelf: "flex-start",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(8px)",
      transition: `opacity 0.35s ease ${delay}ms, transform 0.35s ease ${delay}ms`,
    }}>
      <span style={{ fontSize: 8.5, color: GD, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

// ─── ExpertCard ────────────────────────────────────────────────────────────────
function ExpertCard({ expert, delay, visible }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: WHT, borderRadius: 9, border: `1px solid ${N2}`,
      padding: "8px 9px", width: 144,
      boxShadow: "0 2px 6px rgba(15,23,42,0.06)",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0)" : "translateX(14px)",
      transition: `opacity 0.38s ease ${delay}ms, transform 0.38s ease ${delay}ms`,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%", background: expert.color,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ fontSize: 8.5, fontWeight: 700, color: WHT }}>{expert.sym}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: N8, marginBottom: 1 }}>{expert.label}</div>
        <div style={{ fontSize: 8, color: N5, marginBottom: 3 }}>{expert.sub}</div>
        <div style={{ background: G + "14", borderRadius: 4, padding: "1px 4px", display: "inline-block", border: `1px solid ${G}30` }}>
          <span style={{ fontSize: 7, color: GD, fontWeight: 700 }}>✓ ID Verified</span>
        </div>
      </div>
    </div>
  );
}

// ─── EngineNode ────────────────────────────────────────────────────────────────
function EngineNode({ step }) {
  const active = step >= 2;
  return (
    <div style={{
      width: 104, height: 104, borderRadius: "50%",
      border: `1.5px solid ${active ? G : N2}`,
      background: active ? GL : N0,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(5,150,105,0.08)",
      transition: "all 0.6s ease",
      animation: active ? "ecoPulse 1.8s ease-in-out infinite" : "none",
    }}>
      <style>{`@keyframes ecoPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}`}</style>
      <div style={{ fontSize: 9, fontWeight: 800, color: active ? GD : N7, letterSpacing: 0.3, textAlign: "center" }}>EcoConnect</div>
      <div style={{ fontSize: 8, color: active ? G : N5, textAlign: "center", marginBottom: 5 }}>Smart Match</div>
      <div style={{ width: 44, height: 1, background: N2, marginBottom: 4 }} />
      {ENG_LINES.map((l, i) => {
        const on = step >= l.minStep;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 3, marginBottom: 2 }}>
            <div style={{ width: 4, height: 4, borderRadius: "50%", background: on ? G : N3, transition: "background 0.4s" }} />
            <span style={{ fontSize: 7.5, color: on ? GD : N5, fontWeight: on ? 600 : 400, transition: "all 0.4s" }}>{l.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── StepBar ───────────────────────────────────────────────────────────────────
function StepBar({ step }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${N1}`, background: WHT }}>
      {STEPS_LABELS.map((lbl, i) => {
        const s = i + 1;
        const done = step > s, cur = step === s;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              border: `1.5px solid ${done ? G : cur ? G : N3}`,
              background: done ? G : WHT,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 5, transition: "all 0.4s",
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: done ? WHT : cur ? GD : N4 }}>
                {done ? "✓" : s}
              </span>
            </div>
            <span style={{ fontSize: 10, fontWeight: 500, color: cur ? GD : done ? G : N4, textAlign: "center" }}>{lbl}</span>
            {i < 3 && (
              <div style={{
                position: "absolute", top: 13,
                left: "55%", right: "-55%",
                height: 1.5, background: done ? G : N2,
                zIndex: -1, transition: "background 0.4s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MatchEngineCard (HeroDemoCard) ────────────────────────────────────────────
function MatchEngineCard() {
  const [step,        setStep]        = useState(1);
  const [score,       setScore]       = useState(0);
  const [tagsVisible, setTagsVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timers = [];

    const run = () => {
      if (cancelled) return;
      setStep(1); setScore(0); setTagsVisible(false);

      timers.push(setTimeout(() => { if (!cancelled) setTagsVisible(true); }, 200));
      timers.push(setTimeout(() => { if (!cancelled) setStep(2); }, T.s1));
      timers.push(setTimeout(() => { if (!cancelled) setStep(3); }, T.s1 + T.s2));
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setStep(4);
        let s = 0;
        const inc = setInterval(() => {
          s = Math.min(s + 3, 97);
          setScore(s);
          if (s >= 97) clearInterval(inc);
        }, 24);
        timers.push(inc);
      }, T.s1 + T.s2 + T.s3));

      timers.push(setTimeout(run, TOTAL));
    };

    run();
    return () => { cancelled = true; timers.forEach(t => clearTimeout(t)); };
  }, []);

  const pc = PILL_CFG[step];
  const sc = STATUS_CFG[step];

  return (
    <div style={{
      width: 460, background: WHT, borderRadius: 20,
      border: `1px solid ${N2}`,
      boxShadow: "0 12px 36px rgba(15,23,42,0.10)",
      overflow: "hidden", fontFamily: "system-ui, sans-serif",
    }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: N0, borderBottom: `1px solid ${N2}`, padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {["#ef4444", "#f59e0b", "#22c55e"].map((c, i) => (
            <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />
          ))}
          <span style={{ fontSize: 11, color: N5, fontWeight: 500, marginLeft: 8 }}>EcoConnect · How It Works</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, background: pc.bg, border: `1px solid ${pc.border}`, borderRadius: 20, padding: "3px 10px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: pc.dot }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: pc.text, letterSpacing: 0.5 }}>{pc.label}</span>
        </div>
      </div>

      {/* Step progress */}
      <StepBar step={step} />

      {/* Main 3-column canvas */}
      <div style={{ display: "flex", alignItems: "center", padding: "22px 20px", minHeight: 230, background: N0, borderBottom: `1px solid ${N1}`, gap: 0 }}>

        {/* Left: brief */}
        <div style={{ flex: 1.1, display: "flex", justifyContent: "flex-end", paddingRight: 2 }}>
          <div style={{
            background: WHT, borderRadius: 10,
            border: `1px solid ${step > 1 ? G + "44" : N2}`,
            padding: 11, width: 118,
            boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
            transition: "border-color 0.4s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <div style={{ width: 22, height: 22, borderRadius: 5, background: GL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>🔒</div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: N8 }}>Your Request</div>
                <div style={{ fontSize: 8.5, color: N4 }}>Private · Secure</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {BRIEF_TAGS.map((t, i) => (
                <TagChip key={i} label={t} delay={150 + i * 100} visible={tagsVisible} />
              ))}
            </div>
          </div>
        </div>

        {/* Connector left */}
        <FlowConnector active={step >= 2} />

        {/* Center: engine */}
        <div style={{ flex: 1.2, display: "flex", justifyContent: "center" }}>
          <EngineNode step={step} />
        </div>

        {/* Connector right */}
        <FlowConnector active={step >= 3} />

        {/* Right: experts */}
        <div style={{ flex: 1.4, display: "flex", justifyContent: "flex-start", paddingLeft: 2 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {EXPERTS.map((e, i) => (
              <ExpertCard key={i} expert={e} delay={100 + i * 200} visible={step >= 3} />
            ))}
          </div>
        </div>

      </div>

      {/* Completion banner — step 4 only */}
      {step === 4 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: GL, borderBottom: `1px solid ${G}30`, padding: "11px 18px" }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: G, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: WHT, fontSize: 14, fontWeight: 700 }}>✓</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: GD }}>Experts Matched & Delivered</div>
            <div style={{ fontSize: 9, color: G, marginTop: 1 }}>Reviewed & approved · Ready in under 48 hours</div>
          </div>
          <div style={{ background: WHT, borderRadius: 8, border: `1px solid ${G}40`, padding: "5px 10px", flexShrink: 0, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: GD }}>{score}%</div>
            <div style={{ fontSize: 8, color: G }}>fit score</div>
          </div>
        </div>
      )}

      {/* Stats strip */}
      <div style={{ display: "flex", borderTop: `1px solid ${N2}` }}>
        {[
          { val: step === 4 ? `${score}%` : "—", lbl: "FIT SCORE",     highlight: step === 4 },
          { val: "<48h",                          lbl: "DELIVERY TIME", mid: true             },
          { val: "ID ✓",                          lbl: "ALL VERIFIED",  green: true           },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: "11px 0", textAlign: "center",
            background: s.highlight ? GL : WHT,
            borderLeft:  s.mid ? `1px solid ${N2}` : "none",
            borderRight: s.mid ? `1px solid ${N2}` : "none",
            transition: "background 0.4s",
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: s.green ? G : s.highlight ? GD : SL, letterSpacing: -0.3 }}>{s.val}</div>
            <div style={{ fontSize: 8.5, color: N4, marginTop: 2, fontWeight: 600, letterSpacing: 0.4 }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Status bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 18px", minHeight: 40,
        background: step === 4 ? GD : N0,
        borderTop: `1px solid ${step === 4 ? GD : N2}`,
        transition: "all 0.4s",
      }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 500, color: step === 4 ? GL : N6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {sc.msg}
        </span>
      </div>

    </div>
  );
}

// ─── Execution Page ────────────────────────────────────────────────────────────
function Execution() {
  const navigate = useNavigate();

  return (
    <main style={{ background: "white", minHeight: "100vh" }}>

      {/* HERO — Split layout */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "80px 56px", display: "flex", alignItems: "center", gap: "64px" }}>

        {/* Left — Text */}
        <div style={{ flex: 1 }}>
          <span style={{
            display: "inline-block", background: "#fff7ed", color: "#ea580c",
            border: "1px solid #fed7aa", borderRadius: "100px",
            padding: "5px 16px", fontSize: "11px", fontWeight: 800,
            fontFamily: "'Sora', sans-serif", letterSpacing: "1.5px",
            textTransform: "uppercase", marginBottom: "24px",
          }}>
            Active Platform
          </span>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 900, color: "#1a1a2e", lineHeight: 1.2, letterSpacing: "-1px", margin: "0 0 20px" }}>
            Ecoconnect Execution Platform
          </h1>
          <p style={{ color: "#6b7280", fontSize: "16px", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.8, margin: "0 0 32px" }}>
            A private sustainability talent and project allocation platform — connecting verified professionals with confidential enterprise opportunities through AI-assisted matching and human-reviewed allocation.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 36 }}>
            {[
              { num: "01", title: "KYC & Credential Verification", desc: "Every professional is identity verified and credential reviewed before accessing opportunities." },
              { num: "02", title: "AI-Assisted Expert Matching",   desc: "AI scoring curates expert recommendations — no open bidding, no marketplace chaos." },
              { num: "03", title: "Confidential Project Allocation", desc: "Structured intake workflows with admin-controlled shortlisting and full confidentiality." },
              { num: "04", title: "Sustainability Skilling",        desc: "EV charging infrastructure training and Green Skill Development certification pathways." },
            ].map((item, i) => (
              <div key={i}
                style={{ display: "flex", gap: 16, padding: "16px 20px", borderRadius: 12, border: "1.5px solid #f1f5f9", transition: "border-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#fed7aa"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "#f1f5f9"}
              >
                <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 12, fontWeight: 900, color: "#ea580c", minWidth: 28 }}>{item.num}</span>
                <div>
                  <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 800, color: "#1a1a2e", marginBottom: 3 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => navigate("/contact")}
              style={{ padding: "13px 28px", background: "#ea580c", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#c2410c"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#ea580c"}
            >
              Request Access
            </button>
            <button
              onClick={() => navigate("/contact")}
              style={{ padding: "13px 28px", background: "white", color: "#1a1a2e", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: "pointer" }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#ea580c"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e5e7eb"}
            >
              Enterprise Enquiry
            </button>
          </div>
        </div>

        {/* Right — Animated Card */}
        <div style={{ flexShrink: 0 }}>
          <MatchEngineCard />
        </div>
      </div>

      {/* Focus Areas */}
      <div style={{ borderTop: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "48px 56px" }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: "#aab0b8", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Sora', sans-serif", marginBottom: 16 }}>KEY FOCUS AREAS</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {["ESG", "Net Zero", "Carbon Accounting", "Sustainability Reporting", "EV Infrastructure", "Climate Workforce Development"].map((area, i) => (
              <span key={i} style={{ background: "#f8fafc", color: "#374151", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

    </main>
  );
}

export default Execution;